import {
  TermsConditionsAuditDto,
  AppliedProductPackagesDTO,
  CompanyDTO,
  TermsAndConditionsDto,
  UpdateCompanyOnboardingDTO,
  ProductDto,
  CompanyStaffSettingsDto,
  CompleteCompanyStaffDto,
  ProductsPackagesV2Dto,
  PricingQuoteDto,
  PricingQuoteDetailsDto,
} from '@inosio/payments-sdk';
import { createSlice } from '@reduxjs/toolkit';
import { ErrorType, RootState } from '../../../app/store';

interface IContract {
  InosioTsCs: TermsAndConditionsDto;
  InosioPrivacyPolicy: TermsAndConditionsDto;
  contractPaysafeCombined: TermsAndConditionsDto;
}

type PMOnboardingState = {
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;
  IsAppReceived: boolean;
  onboarding: UpdateCompanyOnboardingDTO;
  contract: IContract;
  defaultPricingOptions: AppliedProductPackagesDTO[] | null;
  totalOwnership: number;
  pmDetails: CompanyStaffSettingsDto; //TODO: remove this to a separate thing.
  isFetchingPmDetailsSuccess: boolean;
  pmDetailsError: string | null;
  isContractOptionFetching: boolean;
  isContractOptionSuccess: boolean;
  isContractOptionError: boolean;
  contractOptionError: string | null;
  isRecordingAgreement: boolean;
  isRecordingAgreementSuccess: boolean;
  isRecordingAgreementFailed: boolean;
  AcceptedTsCsAudit: TermsConditionsAuditDto | null;
  ProductOptions: ProductDto[];
  // product packages V2
  isFetchingProductsPackages: boolean;
  isFetchingProductsPackagesSuccess: boolean;
  isFetchingProductsPackagesFailure: boolean;
  errorFetchingProductsPackages: ErrorType | null;
  productsPackagesV2: ProductsPackagesV2Dto | undefined;

  /**
   * This is the the object that will be used to create a new pricing quote
   * This will be built based on user selections in the pricing wizard
   */
  PricingQuoteSetupObj: PricingQuoteDetailsDto;
  // creating pricing quote
  isCreatingPricingQuote: boolean;
  isCreatingPricingQuoteSuccess: boolean;
  isCreatingPricingQuoteFailure: boolean;
  createPricingQuoteResponse: PricingQuoteDto | null;
  errorCreatingPricingQuote: ErrorType | null;
  // updating pricing quote
  isUpdatingPricingQuote: boolean;
  isUpdatingPricingQuoteSuccess: boolean;
  isUpdatingPricingQuoteFailure: boolean;
  updatePricingQuoteResponse: PricingQuoteDto | null;
  errorUpdatingPricingQuote: ErrorType | null;
  // fetching pricing quote
  isFetchingPricingQuote: boolean;
  isFetchingPricingQuoteSuccess: boolean;
  isFetchingPricingQuoteFailure: boolean;
  fetchPricingQuoteResponse: PricingQuoteDto | null;
  errorFetchingPricingQuote: ErrorType | null;
  // downloading pricing quote
  isDownloadingPricingQuote: boolean;
  isDownloadingPricingQuoteSuccess: boolean;
  isDownloadingPricingQuoteFailure: boolean;
  errorDownloadingPricingQuote: ErrorType | null;
};

const getDefaultState = () => {
  const defaultState: PMOnboardingState = {
    IsAppReceived: false,
    isFetching: false,
    isSuccess: false,
    isError: false,
    errorMessage: null,
    totalOwnership: 0,
    pmDetailsError: null,
    isFetchingPmDetailsSuccess: false,
    pmDetails: {
      CompanyStaff: {} as CompleteCompanyStaffDto,
      Company: {} as CompanyDTO,
      FeaturesEnabled: [],
    },
    contract: {
      InosioTsCs: { Version: '', Description: '', Text: '', TermsConditionsTypeId: 0 },
      InosioPrivacyPolicy: { Version: '', Description: '', Text: '', TermsConditionsTypeId: 0 },
      contractPaysafeCombined: { Version: '', Description: '', Text: '', TermsConditionsTypeId: 0 },
    },
    onboarding: {} as UpdateCompanyOnboardingDTO,
    defaultPricingOptions: null,
    isContractOptionFetching: false,
    isContractOptionSuccess: false,
    isContractOptionError: false,
    contractOptionError: null,
    isRecordingAgreement: false,
    isRecordingAgreementSuccess: false,
    isRecordingAgreementFailed: false,
    AcceptedTsCsAudit: null,
    ProductOptions: [],
    //products packages v2
    isFetchingProductsPackages: false,
    isFetchingProductsPackagesSuccess: false,
    isFetchingProductsPackagesFailure: false,
    errorFetchingProductsPackages: null,
    productsPackagesV2: undefined,
    // creating pricing quote
    isCreatingPricingQuote: false,
    isCreatingPricingQuoteSuccess: false,
    isCreatingPricingQuoteFailure: false,
    createPricingQuoteResponse: null,
    errorCreatingPricingQuote: null,
    // updating pricing quote
    isUpdatingPricingQuote: false,
    isUpdatingPricingQuoteSuccess: false,
    isUpdatingPricingQuoteFailure: false,
    updatePricingQuoteResponse: null,
    errorUpdatingPricingQuote: null,
    // fetching pricing quote
    isFetchingPricingQuote: false,
    isFetchingPricingQuoteSuccess: false,
    isFetchingPricingQuoteFailure: false,
    fetchPricingQuoteResponse: null,
    errorFetchingPricingQuote: null,
    PricingQuoteSetupObj: {
      SelectedPackageUuid: '',
      SelectedPackageLabel: '',
      UnitCount: 0,
      SelectedPackageDiscount: 0,
      IncludedProducts: [],
    },
    isDownloadingPricingQuote: false,
    isDownloadingPricingQuoteSuccess: false,
    isDownloadingPricingQuoteFailure: false,
    errorDownloadingPricingQuote: null,
  };

  return defaultState;
};

const initialState = (): PMOnboardingState => {
  return getDefaultState();
};

export const pmOnboardingSlice = createSlice({
  name: 'pmOnboardingSlice',
  initialState: initialState(),
  reducers: {
    getStartOnboardingApplication(state, action) {
      state.isFetching = true;
      state.errorMessage = null;
      state.isError = false;
      state.isSuccess = false;
    },
    setStartOnboardingApplication: (state, action) => {
      state.isFetching = true;
      state.errorMessage = null;
      state.isError = false;
      state.isSuccess = true;
      // state.isContact=true;
      const newObj = { ...state.onboarding, ...action.payload };
      state.onboarding = newObj;
    },
    setOnboardingApplicationError: (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = action.payload.message;

      // specifically to track pricing plan updates
      state.isContractOptionFetching = true;
      state.isContractOptionSuccess = false;
      state.isContractOptionError = true;
      state.contractOptionError = action.payload.message;
    },
    getUpdateOnboardingApplication(state, action) {
      state.isFetching = true;
      state.errorMessage = null;
      state.isError = false;

      // specifically to track pricing plan updates
      state.isContractOptionFetching = true;
      state.isContractOptionSuccess = false;
      state.isContractOptionError = false;
      state.contractOptionError = null;
    },
    setUpdateOnboardingApplication: (state, action) => {
      state.isFetching = false;
      state.errorMessage = null;
      state.isError = false;
      state.isSuccess = true;
      state.IsAppReceived = true; //app received successfully.

      // track pricing plan updates
      state.isContractOptionFetching = false;
      state.isContractOptionSuccess = true;
    },
    updateOnboardingDto(state, action) {
      state.onboarding = { ...state.onboarding, ...action.payload };
    },
    clearOnboardingApplicationFlags: (state, action) => {
      state.isFetching = false;
      state.errorMessage = null;
      state.isError = false;
      state.isSuccess = false;
    },
    updateIndividualBeneficialOwner(state, action) {
      let benOwners = [...state.onboarding?.BeneficialOwners!];
      benOwners[action.payload.index] = { ...benOwners[action.payload.index], ...action.payload.data };
      let onboardingDto = {
        BeneficialOwners: benOwners,
      };
      const newObj = { ...state.onboarding, ...onboardingDto } as UpdateCompanyOnboardingDTO;
      state.onboarding = newObj;
    },
    getPmDetails(state) {
      state.isFetching = true;
      state.isFetchingPmDetailsSuccess = false;
      state.errorMessage = null;
    },
    setPmDetails(state, action) {
      state.isFetching = false;
      state.isFetchingPmDetailsSuccess = true;
      state.pmDetails = { ...state.pmDetails, ...action.payload };
    },
    setPmDetailsError(state, action) {
      state.isFetchingPmDetailsSuccess = false;
      state.pmDetailsError = action.payload.message;
    },
    getInosioTermsAndConditions(state, action) {
      state.isFetching = true;
      state.errorMessage = null;
    },
    setInosioTermAndConditions: (state, action) => {
      state.isFetching = false;
      state.contract.InosioTsCs = action.payload;
    },
    getInosioPrivacyPolicy(state, action) {
      state.isFetching = true;
      state.errorMessage = null;
    },
    setInosioPrivacyPolicy: (state, action) => {
      state.isFetching = false;
      state.contract.InosioPrivacyPolicy = action.payload;
    },
    getPaysafeCCTermsAndConditions(state, action) {
      state.isFetching = true;
      state.errorMessage = null;
    },
    setPaysafeCCTermAndConditions: (state, action) => {
      state.isFetching = false;
      state.contract.contractPaysafeCombined = action.payload;
    },
    getRecordAgreementDetails(state, action) {
      state.isRecordingAgreement = true;
      state.isRecordingAgreementSuccess = false;
      state.isRecordingAgreementFailed = false;
      state.AcceptedTsCsAudit = null;
      state.errorMessage = null;
    },
    /**
     * Question: Why couldn't you just have one reducer and dispatch it for each type of Term (employment, rental, background check etc)
     * Answer: The problem arose when we merged employment and rental terms into one. The saga handles only one reducer at a time.
     * Therefore, when you dispatch two actions of the same type almost simultaneously, only one of them gets processed by the saga. Solution is
     * to have one reducer that takes in a combined payload and handles both requests.
     */
    batchRecordAgreementDetails(state, action) {
      state.isRecordingAgreement = true;
      state.isRecordingAgreementSuccess = false;
      state.isRecordingAgreementFailed = false;
      state.AcceptedTsCsAudit = null;
      state.errorMessage = null;
    },
    setRecordAgreementDetailsSuccess(state, action) {
      state.isRecordingAgreement = false;
      state.isRecordingAgreementSuccess = true;
      state.isRecordingAgreementFailed = false;
      state.AcceptedTsCsAudit = action.payload;
      state.errorMessage = null;
    },
    setRecordAgreementDetailsFailed(state, action) {
      state.isRecordingAgreement = false;
      state.isRecordingAgreementSuccess = false;
      state.isRecordingAgreementFailed = true;
      state.errorMessage = action.payload.message;
    },
    getOnboardingContractRateOptions(state, action) {
      state.isFetching = true;
      state.errorMessage = null;
      state.isError = false;
    },
    setOnboardingContractRateOptions: (state, action) => {
      state.isFetching = false;
      state.errorMessage = null;
      state.isError = false;
      state.defaultPricingOptions = action.payload;
    },
    getProducts(state, action) {
      state.isFetching = true;
      state.errorMessage = null;
      state.isError = false;
    },
    setProducts: (state, action) => {
      state.isFetching = false;
      state.errorMessage = null;
      state.isError = false;
      state.ProductOptions = action.payload;
    },
    getOnboardingAppCompletedSuccessful(state, action) {
      state = getDefaultState(); //life is good, setting state back to original/default and moving on
      return { ...state };
    },
    getResetOnboardingState(state, action) {
      return {
        ...getDefaultState(),
        contract: state.contract,
      };
    },
    fetchProductsPackagesV2(state) {
      state.isFetchingProductsPackages = true;
      state.isFetchingProductsPackagesSuccess = false;
      state.isFetchingProductsPackagesFailure = false;
      state.errorFetchingProductsPackages = null;
    },
    setProductsPackagesV2(state, action) {
      state.isFetchingProductsPackages = false;
      state.isFetchingProductsPackagesSuccess = true;
      state.isFetchingProductsPackagesFailure = false;
      state.errorFetchingProductsPackages = null;
      state.productsPackagesV2 = action.payload;
    },
    setErrorFetchingProductsPackagesV2(state, action) {
      state.isFetchingProductsPackages = false;
      state.isFetchingProductsPackagesSuccess = false;
      state.isFetchingProductsPackagesFailure = true;
      state.errorFetchingProductsPackages = action.payload;
    },
    createPricingQuote(state) {
      state.isCreatingPricingQuote = true;
      state.isCreatingPricingQuoteFailure = false;
      state.isCreatingPricingQuoteSuccess = false;
      state.errorCreatingPricingQuote = null;
    },
    setCreatePricingQuoteFailed(state, action) {
      state.isCreatingPricingQuote = false;
      state.isCreatingPricingQuoteFailure = true;
      state.isCreatingPricingQuoteSuccess = false;
      state.errorCreatingPricingQuote = action.payload;
    },
    setCreatePricingQuoteSuccess(state, action) {
      state.isCreatingPricingQuote = false;
      state.isCreatingPricingQuoteFailure = false;
      state.isCreatingPricingQuoteSuccess = true;
      state.createPricingQuoteResponse = action.payload;
    },
    updatePricingQuote(state) {
      state.isUpdatingPricingQuote = true;
      state.isUpdatingPricingQuoteFailure = false;
      state.isUpdatingPricingQuoteSuccess = false;
      state.errorUpdatingPricingQuote = null;
    },
    setUpdatePricingQuoteFailed(state, action) {
      state.isUpdatingPricingQuote = false;
      state.isUpdatingPricingQuoteFailure = true;
      state.isUpdatingPricingQuoteSuccess = false;
      state.errorUpdatingPricingQuote = action.payload;
    },
    setUpdatePricingQuoteSuccess(state, action) {
      state.isUpdatingPricingQuote = false;
      state.isUpdatingPricingQuoteFailure = false;
      state.isUpdatingPricingQuoteSuccess = true;
      state.updatePricingQuoteResponse = action.payload;
    },
    fetchPricingQuote(state) {
      state.isFetchingPricingQuote = true;
      state.isFetchingPricingQuoteFailure = false;
      state.isFetchingPricingQuoteSuccess = false;
      state.errorFetchingPricingQuote = null;
    },
    setFetchPricingQuoteFailed(state, action) {
      state.isFetchingPricingQuote = false;
      state.isFetchingPricingQuoteFailure = true;
      state.isFetchingPricingQuoteSuccess = false;
      state.errorFetchingPricingQuote = action.payload;
    },
    setFetchPricingQuoteSuccess(state, action) {
      state.isFetchingPricingQuote = false;
      state.isFetchingPricingQuoteFailure = false;
      state.isFetchingPricingQuoteSuccess = true;
      state.fetchPricingQuoteResponse = action.payload;
    },
    buildPricingQuoteDto(state, action) {
      const existingObj = state.PricingQuoteSetupObj;
      state.PricingQuoteSetupObj = { ...existingObj, ...action.payload };
    },
    resetPricingQuoteSetupObject(state) {
      state.PricingQuoteSetupObj = {
        SelectedPackageUuid: '',
        SelectedPackageLabel: '',
        UnitCount: 0,
        SelectedPackageDiscount: 0,
        IncludedProducts: [],
      };
    },
    resetPricingQuoteStates(state) {
      state.isCreatingPricingQuoteSuccess = false;
    },
    resetUpdatePricingQuoteStates(state) {
      state.isUpdatingPricingQuoteSuccess = false;
    },
    downloadPricingQuote(state) {
      state.isDownloadingPricingQuote = true;
      state.isDownloadingPricingQuoteFailure = false;
      state.isDownloadingPricingQuoteSuccess = false;
    },
    setDownloadPricingQuoteSuccess(state) {
      state.isDownloadingPricingQuote = false;
      state.isDownloadingPricingQuoteFailure = false;
      state.isDownloadingPricingQuoteSuccess = true;
    },
    setDownloadPricingQuoteFailed(state, action) {
      state.isDownloadingPricingQuote = false;
      state.isDownloadingPricingQuoteFailure = true;
      state.isDownloadingPricingQuoteSuccess = false;
      state.errorDownloadingPricingQuote = action.payload;
    },
  },
});

export const {
  getStartOnboardingApplication,
  setStartOnboardingApplication,
  setOnboardingApplicationError,
  getUpdateOnboardingApplication,
  setUpdateOnboardingApplication,
  getOnboardingContractRateOptions,
  setOnboardingContractRateOptions,
  updateOnboardingDto,
  clearOnboardingApplicationFlags,
  updateIndividualBeneficialOwner,
  getInosioTermsAndConditions,
  setInosioTermAndConditions,
  getInosioPrivacyPolicy,
  setInosioPrivacyPolicy,
  getRecordAgreementDetails,
  setRecordAgreementDetailsSuccess,
  setRecordAgreementDetailsFailed,
  getPaysafeCCTermsAndConditions,
  setPaysafeCCTermAndConditions,
  getOnboardingAppCompletedSuccessful,
  getPmDetails,
  setPmDetails,
  setPmDetailsError,
  getResetOnboardingState,
  getProducts,
  setProducts,
  batchRecordAgreementDetails,
  fetchProductsPackagesV2,
  setProductsPackagesV2,
  setErrorFetchingProductsPackagesV2,
  createPricingQuote,
  setCreatePricingQuoteFailed,
  setCreatePricingQuoteSuccess,
  fetchPricingQuote,
  setFetchPricingQuoteFailed,
  setFetchPricingQuoteSuccess,
  updatePricingQuote,
  setUpdatePricingQuoteFailed,
  setUpdatePricingQuoteSuccess,
  buildPricingQuoteDto,
  resetPricingQuoteSetupObject,
  resetPricingQuoteStates,
  resetUpdatePricingQuoteStates,
  downloadPricingQuote,
  setDownloadPricingQuoteFailed,
  setDownloadPricingQuoteSuccess,
} = pmOnboardingSlice.actions;

export default pmOnboardingSlice.reducer;
export const selectOnboardingApplicationState = (state: RootState) => state.pmOnboarding;
